import { IconButton, CheckIcon, EditIcon, Paragraph, RichTextDisplay } from "@datagame/ui";
import { TriviaQuestion } from "../../../../../../../../gql/graphql";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { Section } from "../../../Common";
import { TooltipWithIcon } from "../../../../../../../shared/ui/TooltipWithIcon";
import { TriviaAnswerOptionInput } from "./TriviaAnswerOptionInput";
import { VisualHelpSection } from "./VisualHelpSection";

import styles from "./TriviaQuestionItem.module.scss";

const cx = classNames.bind(styles);

export interface TriviaQuestionItemProps {
  question: TriviaQuestion;
}

export const TriviaQuestionItem = ({ question }: TriviaQuestionItemProps) => {
  const { t: translation } = useTranslation("trivia");
  const t = (key: string) => translation(`advancedSettings.${key}`) as string;
  const [isEditing, setIsEditing] = useState(false);
  const [checkedOptionId, setCheckedOptionId] = useState<string | undefined>(
    question.options.find((o) => o.isCorrect)?.id,
  );

  const sortedOptions = useMemo(() => [...question.options].sort((a, b) => (a.id > b.id ? 1 : -1)), [question.options]);

  const toggleIsEditing = () => {
    setIsEditing(!isEditing);
  };

  return (
    <div className={cx("attribute", { isEditing })}>
      <div className={styles.nameWrapper}>
        <div className={styles.attributeName}>
          <div className={styles.title}>{t("title")}</div>
          <Paragraph size="small" weight="font-bold" color="secondary" className={styles.name}>
            <RichTextDisplay value={question.question} plain={true}/>
          </Paragraph>
        </div>

        <IconButton
          icon={isEditing ? <CheckIcon /> : <EditIcon />}
          className={cx(isEditing ? "outline" : "editIcon")}
          onClick={toggleIsEditing}
        />
      </div>

      {isEditing && (
        <>
          <VisualHelpSection question={question} />

          <Section
            className={styles.answerOptionsSection}
            title={t("answers.section.title")}
            tooltip={<TooltipWithIcon content={t("answers.section.tooltip")} />}
          >
            {sortedOptions.map((option, index) => (
              <div key={option.id}>
                <TriviaAnswerOptionInput
                  key={option.id}
                  option={option}
                  questionId={question.id}
                  currentActiveId={checkedOptionId}
                  onCurrentOptionChange={setCheckedOptionId}
                />

                {index !== question.options.length - 1 && <div className={styles.divider} />}
              </div>
            ))}
          </Section>
        </>
      )}
    </div>
  );
};
